import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  FileText, Calendar, Eye, EyeOff, Edit, Image as ImageIcon, 
  File, Download, AlertTriangle, CheckCircle, XCircle, 
  Clock, Building, Hash, User, MapPin, FileImage, Plus,
  RefreshCw, Loader2, Save, X, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import GlobalHeader from '@/components/GlobalHeader';
import ImageLightbox from '@/components/documents/ImageLightbox';
import API from '@/utils/api';
import { getFileUrl, isFileStorageUrl } from '@/utils/fileStorage';

const CATEGORY_LABELS = {
  ids_travel: 'IDs & Travel',
  insurance: 'Insurance',
  car_registration: 'Car',
  licenses_certs: 'Licenses',
  home_utilities: 'Home',
  work_school: 'Work',
  other: 'Other'
};

const CATEGORY_COLORS = {
  ids_travel: { bg: '#E8EEF4', text: '#4A5568' },
  insurance: { bg: '#DBEAFE', text: '#1E40AF' },
  car_registration: { bg: '#FFE4B8', text: '#9C4221' },
  licenses_certs: { bg: '#EDE9F6', text: '#5B4B8A' },
  home_utilities: { bg: '#E0F4E8', text: '#2D6A4F' },
  work_school: { bg: '#FCE4E4', text: '#9B2C2C' },
  other: { bg: '#E0DCD3', text: '#57534e' }
};

const EXTRACTION_STATUS = {
  not_processed: { label: 'Not Processed', color: 'bg-gray-100 text-gray-600', icon: Clock },
  processing: { label: 'Processing...', color: 'bg-blue-100 text-blue-600', icon: Loader2 },
  processed: { label: 'Processed', color: 'bg-green-100 text-green-600', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-600', icon: XCircle }
};

const DocumentDetailPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [document, setDocument] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSensitive, setShowSensitive] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDocument();
    
    return () => {
      attachments.forEach(a => {
        if (a?.url?.startsWith('blob:')) {
          URL.revokeObjectURL(a.url);
        }
      });
    };
  }, [documentId]);

  const fetchDocument = async () => {
    try {
      const response = await API.get(`/documents/${documentId}`);
      setDocument(response.data);
      
      if (response.data.attachments && response.data.attachments.length > 0) {
        const attachmentData = await Promise.all(
          response.data.attachments.map(async (fileIdOrUrl) => {
            try {
              // Check if this is a base64 data URL - handle directly without fetching
              if (fileIdOrUrl.startsWith('data:')) {
                // Extract content type from data URL (e.g., data:image/png;base64,...)
                const matches = fileIdOrUrl.match(/^data:([^;]+);base64,/);
                const contentType = matches ? matches[1] : 'application/octet-stream';
                const isImage = contentType.startsWith('image/');
                const isPdf = contentType === 'application/pdf';
                
                return {
                  id: fileIdOrUrl.substring(0, 50), // Truncate for ID
                  url: fileIdOrUrl,
                  filename: `document.${contentType.split('/')[1] || 'bin'}`,
                  isImage,
                  isPdf,
                  contentType
                };
              }
              
              // Check if this is a new file storage URL
              if (isFileStorageUrl(fileIdOrUrl)) {
                // New format - use direct URL
                const fullUrl = getFileUrl(fileIdOrUrl);
                const filename = fileIdOrUrl.split('/').pop() || 'document';
                const extension = filename.split('.').pop()?.toLowerCase() || '';
                
                return {
                  id: fileIdOrUrl,
                  url: fullUrl,
                  filename: filename,
                  isImage: ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension),
                  isPdf: extension === 'pdf',
                  contentType: extension === 'pdf' ? 'application/pdf' : `image/${extension}`
                };
              }
              
              // Old format - fetch as blob
              const fileResponse = await API.get(`/documents/file/${fileIdOrUrl}`, {
                responseType: 'blob'
              });
              const blobUrl = URL.createObjectURL(fileResponse.data);
              const contentType = fileResponse.headers['content-type'] || '';
              
              return {
                id: fileIdOrUrl,
                url: blobUrl,
                filename: fileIdOrUrl.split('_').pop() || 'document',
                isImage: contentType.startsWith('image/') || fileIdOrUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i),
                isPdf: contentType === 'application/pdf' || fileIdOrUrl.match(/\.pdf$/i),
                contentType
              };
            } catch (err) {
              console.error('Failed to load attachment:', fileIdOrUrl, err);
              return null;
            }
          })
        );
        setAttachments(attachmentData.filter(Boolean));
      }
    } catch (error) {
      console.error('Failed to fetch document:', error);
      toast.error('Failed to load document');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    await fetchDocument();
    toast.success('Data refreshed');
  }, [documentId]);

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const response = await API.post(`/documents/${documentId}/extract`);
      if (response.data.success) {
        toast.success('Document info extracted successfully!');
        setDocument(response.data.document);
      } else {
        toast.error(response.data.error || 'Extraction failed');
      }
    } catch (error) {
      console.error('Extraction failed:', error);
      toast.error('Failed to extract document info');
    } finally {
      setExtracting(false);
    }
  };

  const startEditing = () => {
    setEditFields({
      full_name: document.full_name || '',
      license_number: document.license_number || document.key_number || '',
      state: document.state || '',
      date_of_birth: document.date_of_birth || '',
      issue_date: document.issue_date || '',
      expiration_date: document.expiration_date || '',
      address: document.address || '',
      license_class: document.license_class || '',
      issuer: document.issuer || '',
      // Insurance
      plan_name: document.plan_name || '',
      group_number: document.group_number || '',
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditFields({});
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      // Clean up empty fields
      const fieldsToSave = {};
      Object.entries(editFields).forEach(([key, value]) => {
        if (value && value.trim()) {
          fieldsToSave[key] = value.trim();
        }
      });
      
      // Also update key_number if license_number is set
      if (fieldsToSave.license_number) {
        fieldsToSave.key_number = fieldsToSave.license_number;
      }
      
      const response = await API.patch(`/documents/${documentId}/fields`, fieldsToSave);
      setDocument(response.data);
      setEditing(false);
      toast.success('Document updated successfully!');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const maskValue = (value) => {
    if (!value) return null;
    if (showSensitive) return value;
    if (value.length <= 4) return '••••';
    return '••••' + value.slice(-4);
  };

  const getStatusConfig = (status) => {
    const configs = {
      active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: XCircle },
      replaced: { label: 'Replaced', color: 'bg-gray-100 text-gray-500', icon: null }
    };
    return configs[status] || configs.active;
  };

  const getDaysUntilExpiry = () => {
    if (!document?.expiration_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(document.expiration_date + 'T00:00:00');
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleAttachmentClick = (attachment, index) => {
    if (attachment.isImage) {
      openLightbox(index);
    } else if (attachment.isPdf) {
      window.open(attachment.url, '_blank');
    } else {
      window.open(attachment.url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50">
        <GlobalHeader
          icon={FileText}
          iconBgColor="#E0DCD3"
          iconColor="text-stone-700"
          title="Document"
          subtitle="Loading..."
          onRefresh={handleRefresh}
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-64 w-full mb-4" />
          <Skeleton className="h-48 w-full mb-4" />
          <Skeleton className="h-32 w-full" />
        </main>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50">
        <GlobalHeader
          icon={FileText}
          iconBgColor="#E0DCD3"
          iconColor="text-stone-700"
          title="Document Not Found"
          subtitle=""
          onRefresh={handleRefresh}
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <p className="text-gray-500">This document could not be found.</p>
          <Button onClick={() => navigate('/documents')} className="mt-4">
            Back to Documents
          </Button>
        </main>
      </div>
    );
  }

  const daysUntilExpiry = getDaysUntilExpiry();
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;
  const statusConfig = getStatusConfig(document.status);
  const categoryColor = CATEGORY_COLORS[document.category_tab] || CATEGORY_COLORS.other;
  const imageAttachments = attachments.filter(a => a.isImage).map(a => ({ url: a.url, filename: a.filename }));
  
  const extractionStatus = document.extraction_status || 'not_processed';
  const extractionConfig = EXTRACTION_STATUS[extractionStatus] || EXTRACTION_STATUS.not_processed;
  const hasExtractedData = document.full_name || document.license_number || document.extracted_fields?.extracted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50" data-testid="document-detail-page">
      <GlobalHeader
        icon={FileText}
        iconBgColor="#E0DCD3"
        iconColor="text-stone-700"
        title={document.title}
        subtitle={CATEGORY_LABELS[document.category_tab] || 'Document'}
        onRefresh={handleRefresh}
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Header Card - Quick Summary */}
        <Card className="border-0 shadow-md overflow-hidden">
          <div 
            className="h-2" 
            style={{ backgroundColor: categoryColor.bg }}
          />
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  <Badge 
                    className={statusConfig.color}
                    data-testid="status-badge"
                  >
                    {statusConfig.icon && <statusConfig.icon className="h-3 w-3 mr-1" />}
                    {statusConfig.label}
                  </Badge>
                  <Badge 
                    style={{ backgroundColor: categoryColor.bg, color: categoryColor.text }}
                  >
                    {CATEGORY_LABELS[document.category_tab] || 'Other'}
                  </Badge>
                  {document.document_type && (
                    <Badge variant="outline">{document.document_type}</Badge>
                  )}
                  {/* Extraction Status Badge */}
                  <Badge className={extractionConfig.color}>
                    {extractionConfig.icon && (
                      <extractionConfig.icon className={`h-3 w-3 mr-1 ${extractionStatus === 'processing' ? 'animate-spin' : ''}`} />
                    )}
                    {extractionConfig.label}
                  </Badge>
                </div>
                
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">{document.title}</h1>
                
                {document.issuer && (
                  <p className="text-gray-600 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    {document.issuer}
                  </p>
                )}
                {document.full_name && (
                  <p className="text-gray-600 flex items-center gap-2 mt-1">
                    <User className="h-4 w-4" />
                    {document.full_name}
                  </p>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSensitive(!showSensitive)}
                  className="flex items-center gap-2"
                >
                  {showSensitive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showSensitive ? 'Hide' : 'Show'}
                </Button>
                {!editing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startEditing}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            </div>

            {/* Expiry Warning */}
            {isExpiringSoon && (
              <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200 text-orange-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <span>Expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</span>
              </div>
            )}
            {isExpired && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200 text-red-700 flex items-center gap-2">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                <span>Expired {Math.abs(daysUntilExpiry)} day{Math.abs(daysUntilExpiry) !== 1 ? 's' : ''} ago</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Extraction Action Card */}
        {attachments.length > 0 && (extractionStatus === 'not_processed' || extractionStatus === 'failed' || !hasExtractedData) && (
          <Card className="border-blue-200 bg-blue-50 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Wand2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">Extract Document Info</p>
                    <p className="text-sm text-blue-700">
                      {extractionStatus === 'failed' 
                        ? 'Previous extraction failed. Try again with a clearer image.'
                        : 'Automatically extract fields from the uploaded image using AI.'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleExtract}
                  disabled={extracting}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {extracting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {extractionStatus === 'failed' ? 'Retry' : 'Extract Info'}
                    </>
                  )}
                </Button>
              </div>
              {extractionStatus === 'failed' && document.extracted_fields?.error && (
                <p className="mt-2 text-sm text-red-600">
                  Error: {document.extracted_fields.error}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Edit Form */}
        {editing && (
          <Card className="border-amber-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Edit className="h-5 w-5 text-amber-500" />
                  Edit Document Fields
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveFields} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={editFields.full_name}
                    onChange={(e) => setEditFields({...editFields, full_name: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="license_number">License / ID Number</Label>
                  <Input
                    id="license_number"
                    value={editFields.license_number}
                    onChange={(e) => setEditFields({...editFields, license_number: e.target.value})}
                    placeholder="DL123456789"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={editFields.state}
                    onChange={(e) => setEditFields({...editFields, state: e.target.value})}
                    placeholder="CA"
                  />
                </div>
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={editFields.date_of_birth}
                    onChange={(e) => setEditFields({...editFields, date_of_birth: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="issue_date">Issue Date</Label>
                  <Input
                    id="issue_date"
                    type="date"
                    value={editFields.issue_date}
                    onChange={(e) => setEditFields({...editFields, issue_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="expiration_date">Expiration Date</Label>
                  <Input
                    id="expiration_date"
                    type="date"
                    value={editFields.expiration_date}
                    onChange={(e) => setEditFields({...editFields, expiration_date: e.target.value})}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={editFields.address}
                    onChange={(e) => setEditFields({...editFields, address: e.target.value})}
                    placeholder="123 Main St, City, State 12345"
                  />
                </div>
                <div>
                  <Label htmlFor="license_class">License Class</Label>
                  <Input
                    id="license_class"
                    value={editFields.license_class}
                    onChange={(e) => setEditFields({...editFields, license_class: e.target.value})}
                    placeholder="C"
                  />
                </div>
                <div>
                  <Label htmlFor="issuer">Issuer / Organization</Label>
                  <Input
                    id="issuer"
                    value={editFields.issuer}
                    onChange={(e) => setEditFields({...editFields, issuer: e.target.value})}
                    placeholder="State of California"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Details Section */}
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Hash className="h-5 w-5 text-stone-400" />
                Details
              </CardTitle>
              {hasExtractedData && extractionStatus === 'processed' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExtract}
                  disabled={extracting}
                  className="text-gray-500"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${extracting ? 'animate-spin' : ''}`} />
                  Reprocess
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Full Name */}
              {document.full_name && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Full Name</p>
                  <p className="font-medium text-lg">{document.full_name}</p>
                </div>
              )}
              
              {/* License Number / Key Number */}
              {(document.license_number || document.key_number) && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">License / ID Number</p>
                  <p className="font-mono text-lg">{maskValue(document.license_number || document.key_number)}</p>
                </div>
              )}
              
              {/* State */}
              {document.state && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">State</p>
                  <p className="font-medium">{document.state}</p>
                </div>
              )}
              
              {/* Date of Birth */}
              {document.date_of_birth && (
                <div className="p-3 bg-pink-50 rounded-lg">
                  <p className="text-xs text-pink-600 uppercase tracking-wide mb-1">Date of Birth</p>
                  <p className="font-medium">
                    {new Date(document.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
              )}
              
              {/* License Class */}
              {document.license_class && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">License Class</p>
                  <p className="font-medium">Class {document.license_class}</p>
                </div>
              )}
              
              {/* Address */}
              {document.address && (
                <div className="p-3 bg-green-50 rounded-lg sm:col-span-2">
                  <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Address</p>
                  <p className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {document.address}
                  </p>
                </div>
              )}
              
              {/* Insurance specific */}
              {document.plan_name && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Plan Name</p>
                  <p className="font-medium">{document.plan_name}</p>
                </div>
              )}
              {document.group_number && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Group Number</p>
                  <p className="font-mono">{maskValue(document.group_number)}</p>
                </div>
              )}
              {document.insurance_type && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Coverage Type</p>
                  <p className="font-medium capitalize">{document.insurance_type}</p>
                </div>
              )}
              
              {/* Car specific */}
              {document.vin && (
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-xs text-orange-600 uppercase tracking-wide mb-1">VIN</p>
                  <p className="font-mono text-sm">{maskValue(document.vin)}</p>
                </div>
              )}
              {document.plate_number && (
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-xs text-orange-600 uppercase tracking-wide mb-1">Plate Number</p>
                  <p className="font-mono">{document.plate_number}</p>
                </div>
              )}
              
              {/* Home specific */}
              {document.account_number && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Account Number</p>
                  <p className="font-mono">{maskValue(document.account_number)}</p>
                </div>
              )}
              {document.service_address && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Service Address</p>
                  <p className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {document.service_address}
                  </p>
                </div>
              )}
              
              {/* Linked person */}
              {document.linked_person && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Linked Person</p>
                  <p className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {document.linked_person}
                  </p>
                </div>
              )}
            </div>
            
            {/* Empty state for details */}
            {!hasExtractedData && !document.key_number && !document.plan_name && !document.vin && !document.account_number && (
              <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
                <Hash className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 mb-2">No details extracted yet</p>
                {attachments.length > 0 ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExtract}
                    disabled={extracting}
                  >
                    {extracting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                    Extract from Image
                  </Button>
                ) : (
                  <p className="text-sm text-gray-400">Upload an image to extract information</p>
                )}
              </div>
            )}
            
            {/* Coverage/Renewal notes */}
            {document.coverage_notes && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Coverage Notes</p>
                <p className="text-gray-700">{document.coverage_notes}</p>
              </div>
            )}
            {document.renewal_instructions && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Renewal Instructions</p>
                <p className="text-gray-700">{document.renewal_instructions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dates & Reminders */}
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-stone-400" />
              Dates & Reminders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {document.issue_date && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Issue Date</p>
                  <p className="font-medium">
                    {new Date(document.issue_date + 'T00:00:00').toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
              )}
              {document.expiration_date && (
                <div className={`p-3 rounded-lg ${isExpired ? 'bg-red-50' : isExpiringSoon ? 'bg-orange-50' : 'bg-gray-50'}`}>
                  <p className={`text-xs uppercase tracking-wide mb-1 ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-orange-600' : 'text-gray-500'}`}>
                    Expiration Date
                  </p>
                  <p className={`font-medium ${isExpired ? 'text-red-700' : isExpiringSoon ? 'text-orange-700' : ''}`}>
                    {new Date(document.expiration_date + 'T00:00:00').toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
              )}
              {document.reminder_days && document.reminder_days.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reminders</p>
                  <p className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    {document.reminder_days.join(', ')} days before
                  </p>
                </div>
              )}
            </div>
            
            {!document.issue_date && !document.expiration_date && (
              <p className="text-gray-400 text-center py-4">No dates recorded</p>
            )}
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileImage className="h-5 w-5 text-stone-400" />
              Attachments
              {attachments.length > 0 && (
                <Badge variant="secondary" className="ml-2">{attachments.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {attachments.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                <ImageIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 mb-3">No attachments yet</p>
                <Button variant="outline" size="sm" className="text-stone-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Attachment
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {attachments.map((attachment, index) => (
                  <div
                    key={attachment.id}
                    onClick={() => handleAttachmentClick(attachment, imageAttachments.findIndex(a => a.url === attachment.url))}
                    className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow aspect-square bg-gray-100"
                    data-testid={`attachment-${index}`}
                  >
                    {attachment.isImage ? (
                      <img
                        src={attachment.url}
                        alt={attachment.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="12">Image</text></svg>';
                        }}
                      />
                    ) : attachment.isPdf ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                        <File className="h-12 w-12 text-red-400 mb-2" />
                        <span className="text-xs text-red-600 font-medium">PDF</span>
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <File className="h-12 w-12 text-gray-400 mb-2" />
                        <span className="text-xs text-gray-500">File</span>
                      </div>
                    )}
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {attachment.isImage ? (
                        <Eye className="h-8 w-8 text-white" />
                      ) : (
                        <Download className="h-8 w-8 text-white" />
                      )}
                    </div>
                    
                    {/* Filename */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs truncate">{attachment.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        {(document.notes || document.extracted_text) && (
          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-stone-400" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {document.notes && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{document.notes}</p>
                </div>
              )}
              {document.extracted_text && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Extracted Text (OCR)</p>
                  <p className="text-gray-600 text-sm whitespace-pre-wrap font-mono">{document.extracted_text}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tags */}
        {document.tags && document.tags.length > 0 && (
          <Card className="border-gray-100 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                {document.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata footer */}
        <div className="text-center text-xs text-gray-400 py-4">
          Created {new Date(document.created_at).toLocaleDateString()} • 
          Updated {new Date(document.updated_at).toLocaleDateString()}
        </div>
      </main>

      {/* Image Lightbox */}
      {lightboxOpen && imageAttachments.length > 0 && (
        <ImageLightbox
          images={imageAttachments}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default DocumentDetailPage;
